// The six renderable **visual kinds**. A visual kind is one standalone image.
// Each reads exactly one mini-PIM **content kind**. More than one visual may
// present the same content kind, but no visual combines content kinds:
//
//   visual `nutrition`             <- content `nutrition`
//   visual `nutrition-multilingual`<- content `nutrition`
//   visual `amino`                 <- content `amino`
//   visual `amino-multilingual`    <- content `amino`
//   visual `etiquette`             <- content `etiquette_fields`  (the attributes image)
//   visual `marketing-ingredients` <- content `marketing-ingredients`
//
// The name `etiquette` stays as-is in code, DB, and storage ("digitálna
// etiketa" is the established term); "attributes image" is its synonym.
//
// `marketing-ingredients` is ONE token for both the visual and the content kind
// (issue #349) — it reaches the DB check constraint, the `?kind=` param, and the
// `product-visual/marketing-ingredients/…` object path. The mirror column and the
// Magento attribute code stay `marketing_ingredients`: those are SQL/EAV
// vocabularies, not the kind vocabulary.
export const PRODUCT_VISUAL_KIND = {
  nutrition: "nutrition",
  nutritionMultilingual: "nutrition-multilingual",
  amino: "amino",
  aminoMultilingual: "amino-multilingual",
  etiquette: "etiquette",
  marketingIngredients: "marketing-ingredients",
} as const

export type ProductVisualKind = (typeof PRODUCT_VISUAL_KIND)[keyof typeof PRODUCT_VISUAL_KIND]

/** Every visual kind, in registry order. A tuple (not `Object.values`) so it can
 *  seed a `z.enum` and keep its literal member types; `template-registry.test.ts`
 *  asserts it stays in sync with the registry. */
export const PRODUCT_VISUAL_KINDS = [
  PRODUCT_VISUAL_KIND.nutrition,
  PRODUCT_VISUAL_KIND.nutritionMultilingual,
  PRODUCT_VISUAL_KIND.amino,
  PRODUCT_VISUAL_KIND.aminoMultilingual,
  PRODUCT_VISUAL_KIND.etiquette,
  PRODUCT_VISUAL_KIND.marketingIngredients,
] as const

export function isProductVisualKind(value: string): value is ProductVisualKind {
  return PRODUCT_VISUAL_KINDS.some((kind) => kind === value)
}
